import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { AvatarPreview } from "../components/AvatarEditor";
import { newStudentId, randomAvatar, rememberStudent, saveStudentRecord, type ClassStudent } from "../classroom/types";

/** Name-list login — tap your name (no typing) or add yourself once. */
export function ClassLogin() {
  const nav = useNavigate();
  const [params] = useSearchParams();
  const code = (params.get("code") ?? "").toUpperCase();
  const [roster, setRoster] = useState<ClassStudent[]>([]);
  const [className, setClassName] = useState("");
  const [onlineIds, setOnlineIds] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [name, setName] = useState("");

  useEffect(() => {
    if (!code) return;
    let alive = true;
    const load = () => {
      fetch(`/class/roster/${code}`)
        .then((r) => r.json() as Promise<{ roster?: ClassStudent[]; className?: string; onlineIds?: string[] }>)
        .then((d) => { if (!alive) return; setRoster(d.roster ?? []); setClassName(d.className ?? ""); setOnlineIds(d.onlineIds ?? []); })
        .catch(() => {})
        .finally(() => { if (alive) setLoading(false); });
    };
    load();
    const id = window.setInterval(load, 4000);
    return () => { alive = false; window.clearInterval(id); };
  }, [code]);

  const goLobby = (id: string) => { rememberStudent(code, id); nav(`/class/lobby?code=${code}&student=${id}`); };
  const pickStudent = (s: ClassStudent) => { saveStudentRecord(code, s.id, s.name, s.avatarConfig); goLobby(s.id); };
  const addStudent = (e: React.FormEvent) => {
    e.preventDefault();
    const nm = name.trim();
    if (!nm) return;
    const id = newStudentId();
    saveStudentRecord(code, id, nm, randomAvatar());
    goLobby(id);
  };

  if (!code) {
    return <div className="class-login"><p>No class code.</p><button className="btn btn-green" onClick={() => nav("/class")}>Enter code</button></div>;
  }

  return (
    <div className="class-login">
      <header className="class-login-top">
        <button className="btn btn-ghost" onClick={() => nav("/class")}>← Class</button>
        <h1 className="class-login-title">{className || "Class"} <small>{code}</small></h1>
      </header>
      <p className="class-login-hint">Tap your name to join</p>
      <div className="class-name-grid">
        {roster.map((s) => (
          <button key={s.id} className={`class-name-card ${onlineIds.includes(s.id) ? "is-online" : ""}`} onClick={() => pickStudent(s)}>
            <span className="class-name-avatar"><AvatarPreview cfg={s.avatarConfig} /></span>
            <strong>{s.name}</strong>
          </button>
        ))}
        <button className="class-name-card class-name-add" onClick={() => setAdding(true)}>
          <span className="class-name-plus" aria-hidden>＋</span>
          <strong>New student</strong>
        </button>
      </div>
      {loading && roster.length === 0 && <p className="class-dim">Loading class…</p>}

      {adding && (
        <div className="class-modal-overlay" onClick={() => setAdding(false)}>
          <form className="class-modal" onClick={(e) => e.stopPropagation()} onSubmit={addStudent}>
            <h2>What's your name?</h2>
            <input className="class-code-input" value={name} onChange={(e) => setName(e.target.value)} placeholder="Name" maxLength={20} autoFocus />
            <button className="btn btn-green btn-big btn-block" type="submit" disabled={!name.trim()}>Join →</button>
            <button className="btn btn-ghost" type="button" onClick={() => setAdding(false)}>Cancel</button>
          </form>
        </div>
      )}
    </div>
  );
}
